import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, MessageSquare, Send, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRow = ({ value, onChange, size = 20, readOnly = false, testId }) => (
  <div className="flex items-center gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= value;
      return (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(n)}
          className={`transition-transform ${
            !readOnly ? 'hover:scale-110 cursor-pointer' : 'cursor-default'
          }`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          data-testid={readOnly ? undefined : `rating-star-${n}`}
        >
          <Star
            style={{ width: size, height: size }}
            className={filled ? 'text-saffron-500 fill-saffron-500' : 'text-gray-300'}
          />
        </button>
      );
    })}
  </div>
);

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

export const TempleReviews = ({ temple }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', rating: 0, text: '' });

  useEffect(() => {
    if (!temple?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/reviews/${temple.id}`);
        if (cancelled) return;
        setReviews(data.reviews || []);
        setStats(data.stats || { avg_rating: 0, count: 0 });
      } catch (err) {
        console.error('Failed to load reviews', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [temple?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.rating < 1) {
      toast.error('Please pick a star rating');
      return;
    }
    if (form.text.trim().length < 20) {
      toast.error('Review must be at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        temple_id: temple.id,
        name: form.name.trim(),
        email: form.email.trim(),
        rating: form.rating,
        text: form.text.trim(),
      });
      setReviews((prev) => [data, ...prev]);
      setStats((prev) => {
        const total = prev.avg_rating * prev.count + data.rating;
        const count = prev.count + 1;
        return { avg_rating: Math.round((total / count) * 100) / 100, count };
      });
      setForm({ name: '', email: '', rating: 0, text: '' });
      toast.success('Thank you! Your review has been posted.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail || 'Failed to submit review';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="py-16 md:py-24 bg-background border-t border-orange-100/60"
      data-testid="temple-reviews-section"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-4xl">
        <div className="mb-10 text-center">
          <div className="inline-flex items-center px-4 py-1 bg-saffron-100 text-saffron-700 font-outfit text-xs uppercase tracking-wider mb-4">
            <MessageSquare className="w-3.5 h-3.5 mr-2" />
            Pilgrim Reviews
          </div>
          <h2 className="font-cormorant text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-temple-slate mb-3">
            What Pilgrims Say About {temple.name}
          </h2>
          {stats.count > 0 ? (
            <div className="flex items-center justify-center gap-3 mt-2" data-testid="reviews-summary">
              <StarRow value={Math.round(stats.avg_rating)} readOnly size={22} />
              <span className="font-cormorant text-2xl font-bold text-temple-slate">
                {stats.avg_rating.toFixed(1)}
              </span>
              <span className="font-outfit text-sm text-gray-600">
                ({stats.count} pilgrim{stats.count === 1 ? '' : 's'})
              </span>
            </div>
          ) : (
            <p className="font-outfit text-base text-gray-600">
              Be the first to share your darshan experience.
            </p>
          )}
        </div>

        {/* Review Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white border border-orange-100/60 p-6 md:p-8 mb-10"
          data-testid="review-form"
        >
          <h3 className="font-cormorant text-2xl font-bold text-temple-slate mb-5">
            Share Your Darshan Experience
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <input
              type="text"
              required
              minLength={2}
              maxLength={60}
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="border border-orange-100 px-4 py-2.5 font-outfit text-sm focus:outline-none focus:border-saffron-500"
              data-testid="review-name-input"
            />
            <input
              type="email"
              required
              placeholder="Email (not shown publicly)"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="border border-orange-100 px-4 py-2.5 font-outfit text-sm focus:outline-none focus:border-saffron-500"
              data-testid="review-email-input"
            />
          </div>

          <div className="mb-4">
            <label className="block font-outfit text-xs uppercase tracking-wider text-gray-500 mb-2">
              Your Rating
            </label>
            <StarRow
              value={form.rating}
              onChange={(v) => setForm({ ...form, rating: v })}
              size={28}
              testId="review-rating-picker"
            />
          </div>

          <textarea
            required
            minLength={20}
            maxLength={2000}
            placeholder="Share the highlights of your pilgrimage — the aarti, the local food, tips for other pilgrims (min 20 characters)"
            rows={5}
            value={form.text}
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            className="w-full border border-orange-100 px-4 py-2.5 font-outfit text-sm focus:outline-none focus:border-saffron-500 mb-2 resize-y"
            data-testid="review-text-input"
          />
          <div className="flex items-center justify-between">
            <span className="font-outfit text-xs text-gray-400">
              {form.text.length}/2000
            </span>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-saffron-500 hover:bg-saffron-600 disabled:bg-saffron-300 text-white font-outfit font-medium px-6 py-2.5 transition-colors"
              data-testid="submit-review-button"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Posting…' : 'Post Review'}
            </button>
          </div>
        </form>

        {/* Reviews List */}
        {loading ? (
          <div className="text-center py-8 font-outfit text-gray-500" data-testid="reviews-loading">
            Loading reviews…
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-8 font-outfit text-gray-500" data-testid="reviews-empty">
            No reviews yet — yours could be the first.
          </div>
        ) : (
          <div className="space-y-4" data-testid="reviews-list">
            {reviews.map((r) => (
              <div
                key={r.id}
                className="bg-white border border-orange-100/60 p-5 md:p-6"
                data-testid={`review-item-${r.id}`}
              >
                <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-saffron-100 text-saffron-600 flex items-center justify-center rounded-full">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-outfit font-medium text-temple-slate">{r.name}</div>
                      <div className="font-outfit text-xs text-gray-500">
                        {formatDate(r.created_at)}
                      </div>
                    </div>
                  </div>
                  <StarRow value={r.rating} readOnly size={16} />
                </div>
                <p className="font-outfit text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {r.text}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
